/**
 * RKL — Retail Kill List.
 *
 * System Ops destructive-cleanup queue. /v3/kill-list/* is live. Two-step
 * verify → execute workflow; Test Mode entries auto-verified.
 * Role-gate: SystemOps + SuperAdmin can verify/execute. FSR/Supervisor = read.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router';
import type { KillListEntryDto, KillEntryType, KillStatus } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button, FilterChips, Pill } from '@/components/primitives';
import { formatLocal } from '@/utils/time';
import { EntryDetail } from './EntryDetail';

type StatusFilter = 'all' | 'pending' | 'verified' | 'executed' | 'cancelled';
type EntryFilter = 'all' | 'cif' | 'account' | 'card' | 'other';

interface ParsedFilters {
  status?: KillStatus;
  entryType?: KillEntryType;
}

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending verify' },
  { value: 'verified', label: 'Verified' },
  { value: 'executed', label: 'Executed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ENTRY_OPTIONS: Array<{ value: EntryFilter; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'cif', label: 'CIF' },
  { value: 'account', label: 'Account' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
];

function toFilters(status: StatusFilter, entry: EntryFilter): ParsedFilters {
  const out: ParsedFilters = {};
  if (status === 'pending') out.status = 'pending-verification';
  else if (status === 'verified') out.status = 'verified';
  else if (status === 'executed') out.status = 'executed';
  else if (status === 'cancelled') out.status = 'cancelled';
  if (entry === 'cif') out.entryType = 'CifKill';
  else if (entry === 'account') out.entryType = 'AccountKill';
  else if (entry === 'card') out.entryType = 'CardRevoke';
  else if (entry === 'other') out.entryType = 'other';
  return out;
}

function Queue(): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [entry, setEntry] = useState<EntryFilter>('all');
  const [entries, setEntries] = useState<ReadonlyArray<KillListEntryDto>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const canMutate = user?.primaryRole === 'SuperAdmin' || user?.primaryRole === 'Supervisor';

  const refresh = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.retail.killList.list(
        { ...toFilters(status, entry), top: 200 },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      setEntries(resp.entries);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load kill list');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [apiClient, status, entry]);

  useEffect(() => {
    void refresh();
    return () => abortRef.current?.abort();
  }, [refresh]);

  const counts = useMemo(() => {
    const byStatus: Record<string, number> = {};
    for (const e of entries) {
      byStatus[e.status] = (byStatus[e.status] ?? 0) + 1;
    }
    return byStatus;
  }, [entries]);

  return (
    <div>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-5)',
          marginBottom: 'var(--sp-6)',
          paddingBottom: 'var(--sp-5)',
          borderBottom: '1px solid var(--surface-border)',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Retail Kill List</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-600)' }}>
            Destructive-cleanup queue for CIFs / Accounts / Cards / NetTeller.
            Two-step verify → execute. Test Mode entries are auto-verified.
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--sp-3)' }}>
          {canMutate ? (
            <Pill variant="ok">You can verify &amp; execute</Pill>
          ) : (
            <Pill variant="muted">Read-only (role {user?.primaryRole ?? '—'})</Pill>
          )}
          <Button variant="ghost" size="sm" onClick={() => void refresh()}>
            Refresh
          </Button>
        </div>
      </header>

      <div style={{ display: 'flex', gap: 'var(--sp-5)', marginBottom: 'var(--sp-5)', flexWrap: 'wrap' }}>
        <FilterChips<StatusFilter>
          label="Status"
          options={STATUS_OPTIONS}
          selected={status}
          onChange={setStatus}
        />
        <FilterChips<EntryFilter>
          label="Entry type"
          options={ENTRY_OPTIONS}
          selected={entry}
          onChange={setEntry}
        />
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            marginBottom: 'var(--sp-5)',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {loading && <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>Loading kill queue…</div>}

      {!loading && entries.length === 0 && (
        <div
          style={{
            padding: 'var(--sp-10)',
            textAlign: 'center',
            background: 'var(--ink-50)',
            border: '1px dashed var(--ink-300)',
            borderRadius: 'var(--r-md)',
            color: 'var(--ink-600)',
            fontSize: 13,
          }}
        >
          No kill list entries match this filter.
        </div>
      )}

      <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
        {entries.map((e) => (
          <button
            key={e.killListEntryId}
            type="button"
            onClick={() => navigate(`/rkl/${encodeURIComponent(e.killListEntryId)}`)}
            style={{
              display: 'grid',
              gridTemplateColumns: '4px 1fr auto auto auto',
              gap: 'var(--sp-5)',
              alignItems: 'center',
              padding: 'var(--sp-5) var(--sp-6)',
              background: 'var(--surface-card)',
              border: '1px solid var(--surface-border)',
              borderLeft: `4px solid ${statusStripe(e.status)}`,
              borderRadius: 'var(--r-md)',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
            }}
          >
            <span aria-hidden />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {entryTypeLabel(e.entryType)} · {e.targetId}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 2 }}>
                Reason: {e.reason} · created {formatLocal(e.createdAt)}
              </div>
            </div>
            {e.testMode && <Pill variant="warn">Test Mode</Pill>}
            <Pill variant={statusPillVariant(e.status)}>{e.status}</Pill>
            <span style={{ fontSize: 11, color: 'var(--ink-500)' }}>
              {e.sourceCartId ? `cart ${e.sourceCartId.slice(0, 8)}…` : '—'}
            </span>
          </button>
        ))}
      </div>

      {entries.length > 0 && (
        <footer
          style={{
            marginTop: 'var(--sp-7)',
            fontSize: 11,
            color: 'var(--ink-500)',
            display: 'flex',
            gap: 'var(--sp-5)',
          }}
        >
          <span>Total: {entries.length}</span>
          {Object.entries(counts).map(([s, c]) => (
            <span key={s}>
              {s}: {c}
            </span>
          ))}
        </footer>
      )}
    </div>
  );
}

function statusStripe(status: KillStatus): string {
  switch (status) {
    case 'executed':
      return 'var(--pcb-green)';
    case 'cancelled':
      return 'var(--ink-400)';
    case 'failed':
      return 'var(--pcb-red)';
    case 'verified':
      return 'var(--pcb-blue)';
    default:
      return 'var(--pcb-amber-text)';
  }
}

function statusPillVariant(status: KillStatus): 'ok' | 'warn' | 'err' | 'info' | 'muted' {
  switch (status) {
    case 'executed':
      return 'ok';
    case 'cancelled':
      return 'muted';
    case 'failed':
      return 'err';
    case 'verified':
      return 'info';
    default:
      return 'warn';
  }
}

function entryTypeLabel(t: KillEntryType): string {
  switch (t) {
    case 'CifKill':
    case 'cif':
      return 'CIF Kill';
    case 'AccountKill':
    case 'account':
      return 'Account Kill';
    case 'CardRevoke':
    case 'card':
      return 'Card Revoke';
    case 'NetTellerRevoke':
      return 'NetTeller Revoke';
    case 'sdb-box':
      return 'SDB Box';
    case 'other':
      return 'Other';
    default:
      return String(t);
  }
}

function DetailRouteWrapper(): JSX.Element {
  const { entryId } = useParams();
  if (!entryId) return <Queue />;
  return <EntryDetail entryId={entryId} />;
}

export default function RKL(): JSX.Element {
  return (
    <Routes>
      <Route index element={<Queue />} />
      <Route path=":entryId" element={<DetailRouteWrapper />} />
    </Routes>
  );
}
